import React, { useState, useEffect, useRef } from 'react';
import { 
  FiSliders, FiActivity, FiWifi, FiWifiOff, FiLoader, FiAlertTriangle, FiCheckCircle, 
  FiTrendingUp, FiTrendingDown, FiDollarSign, FiZap, FiBarChart2, FiThumbsUp,
  FiRefreshCw, FiPlay
} from 'react-icons/fi';
import toast from 'react-hot-toast';

// Helpers définis en dehors du composant
const formatCurrency = (amount, digits = 0) => {
  if (amount === null || amount === undefined || isNaN(amount)) return 'N/A';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount);
};

const ResultCard = ({ title, value, unit, colorClass, icon }) => (
  <div className={`bg-white p-1.5 rounded border-l-4 ${colorClass}`}>
    <div className="flex items-center">
      <div className={`p-1 rounded mr-2 ${colorClass.replace('border-', 'bg-').replace('text-', 'bg-')} bg-opacity-10`}>
        {React.cloneElement(icon, { className: `h-3 w-3 ${colorClass.replace('border-', 'text-')}` })}
      </div>
      <div>
        <p className="text-xs text-gray-600">{title}</p>
        <p className="text-xs font-bold text-gray-900">{value} <span className="text-xs font-normal">{unit}</span></p>
      </div>
    </div>
  </div>
);

// Default conservateur si currentPowers vraiment manquant (cas rare, fallback).
// Valeur arbitraire mais réaliste pour BT_SUP_36 ; le caller doit passer
// les vraies PS depuis composantes_detail.CS_FIXE.parametres.
const DEFAULT_POWERS = { hph: 36, hch: 36, hpb: 36, hcb: 36 };

const ManualOptimizationPanel = ({ pdlData, results, session, currentPowers = DEFAULT_POWERS, onOptimizationUpdate, savedStrategy }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  // Si une stratégie manuelle est déjà sauvegardée, on restaure les puissances
  // depuis l'objet sauvegardé (sinon on part de currentPowers).
  const savedPowers = savedStrategy?.power_adjustments?.reduce((acc, adj) => {
    const key = (adj.period || '').toLowerCase();
    if (['hph', 'hch', 'hpb', 'hcb'].includes(key)) acc[key] = Number(adj.new_value);
    return acc;
  }, {}) || null;
  const initialPowers = savedPowers && Object.keys(savedPowers).length === 4 ? savedPowers : {
    hph: currentPowers?.hph || DEFAULT_POWERS.hph,
    hch: currentPowers?.hch || DEFAULT_POWERS.hch,
    hpb: currentPowers?.hpb || DEFAULT_POWERS.hpb,
    hcb: currentPowers?.hcb || DEFAULT_POWERS.hcb
  };
  const [powers, setPowers] = useState(initialPowers);
  const [optimizationResult, setOptimizationResult] = useState(savedStrategy || null);
  const [loading, setLoading] = useState(false);
  const [realTimeData, setRealTimeData] = useState(null);
  // Indique si le user a déjà validé / sauvegardé ce choix
  const [isValidated, setIsValidated] = useState(!!savedStrategy);
  // Marge de sécurité au-dessus du max atteint, en % (0 = exactement le max).
  const [safetyMarginPct, setSafetyMarginPct] = useState(10);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const socketCreatedRef = useRef(false);

  // Sync powers ← currentPowers quand celles-ci arrivent en async (cas typique
  // où currentPowers vient d'un fetch parent qui termine après le 1er render).
  useEffect(() => {
    if (currentPowers?.hph != null) {
      setPowers({
        hph: currentPowers.hph,
        hch: currentPowers.hch ?? currentPowers.hph,
        hpb: currentPowers.hpb ?? currentPowers.hph,
        hcb: currentPowers.hcb ?? currentPowers.hph,
      });
    }
  }, [currentPowers?.hph, currentPowers?.hch, currentPowers?.hpb, currentPowers?.hcb]);

  // Puissance max atteinte par cadran. Même logique que SyntheseTab : on
  // privilégie analytics.puissance_max_*_kw (par cadran, calcul moteur), sinon
  // on reconstruit depuis depassements_detectes, sinon heuristique sur max global
  // (HPH=1, HCH=0.8, HPB=0.9, HCB=0.7) pour éviter d'afficher la même valeur
  // sur les 4 cadrans (réalité physique : profil de conso plus chargé en hiver
  // pointe / journée que basse saison nuit).
  const maxPowersByPeriod = (() => {
    const analytics = results?.turpe6_details?.details_calcul?.analytics || {};
    const depassements = results?.turpe6_details?.depassements_detectes || [];
    let fromAnalytics = {
      hph: Number(analytics.puissance_max_hph_kw) || 0,
      hch: Number(analytics.puissance_max_hch_kw) || 0,
      hpb: Number(analytics.puissance_max_hpb_kw) || 0,
      hcb: Number(analytics.puissance_max_hcb_kw) || 0,
    };
    if (Object.values(fromAnalytics).every(v => v === 0) && depassements.length > 0) {
      depassements.forEach(d => {
        const p = String(d.period || '').toLowerCase();
        const v = Number(d.puissance_max_kw) || 0;
        if (fromAnalytics[p] != null && v > fromAnalytics[p]) fromAnalytics[p] = v;
      });
    }
    const maxGlobal = Number(analytics.puissance_max_kw) || 0;
    if (Object.values(fromAnalytics).every(v => v === 0) && maxGlobal > 0) {
      // Heuristique alignée sur SyntheseTab : profil typique BT (hiver pointe > basse saison nuit).
      fromAnalytics = {
        hph: maxGlobal,
        hch: maxGlobal * 0.8,
        hpb: maxGlobal * 0.9,
        hcb: maxGlobal * 0.7,
      };
    }
    return fromAnalytics;
  })();

  const pdlNumber = pdlData?.pdl_number;
  // Fallback session.user.id pour les PDLs SFTP sans pdl.user_id legacy.
  const userId = pdlData?.user_id || session?.user?.id;

  useEffect(() => {
    if (pdlNumber && userId && !socketCreatedRef.current) {
      socketCreatedRef.current = true;
      connectWebSocket();
    }
    
    return () => {
      disconnectWebSocket();
      socketCreatedRef.current = false;
    };
  }, [userId, pdlNumber]);

  const connectWebSocket = () => {
    try {
      setConnectionStatus('connecting');
      const wsUrl = `/ws/optimize-realtime/${userId}/${pdlNumber}`;
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        setIsConnected(true);
        setConnectionStatus('connected');
        toast.success('Connexion temps réel établie');
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleWebSocketMessage(message);
        } catch (error) {
          console.error('Erreur parsing message WebSocket:', error);
        }
      };

      wsRef.current.onclose = () => {
        setIsConnected(false);
        setConnectionStatus('disconnected');
        
        if (socketCreatedRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 3000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('Erreur WebSocket:', error);
        setConnectionStatus('error');
        toast.error('Erreur de connexion temps réel');
      };

    } catch (error) {
      console.error('Erreur connexion WebSocket:', error);
      setConnectionStatus('error');
    }
  };

  const disconnectWebSocket = () => {
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    const socketToClose = wsRef.current;
    if (socketToClose) {
      socketToClose.onopen = null;
      socketToClose.onmessage = null;
      socketToClose.onclose = null;
      socketToClose.onerror = null;
      socketToClose.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    setConnectionStatus('disconnected');
  };

  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'connection_established':
        toast.success('Connexion temps réel établie');
        break;
      case 'optimization_update':
        // Update temps réel (slider) → on stocke localement pour affichage.
        // La sauvegarde en BDD est explicite via le bouton "Valider et enregistrer".
        setRealTimeData(message.data);
        break;
      case 'calculation_error':
        toast.error(`Erreur calcul: ${message.error}`);
        break;
      default:
        break;
    }
  };

  const sendPowerAdjustment = (newPowers) => {
    if (!isConnected || !wsRef.current) {
      toast.error('Connexion temps réel non disponible');
      return;
    }
    const message = {
      type: 'power_adjustment',
      data: {
        hph: parseFloat(newPowers.hph),
        hch: parseFloat(newPowers.hch),
        hpb: parseFloat(newPowers.hpb),
        hcb: parseFloat(newPowers.hcb)
      }
    };
    wsRef.current.send(JSON.stringify(message));
  };

  const handlePowerChange = (period, value) => {
    const newPowers = { ...powers, [period]: parseFloat(value) };
    setPowers(newPowers);
    clearTimeout(window.powerChangeTimeout);
    window.powerChangeTimeout = setTimeout(() => {
      sendPowerAdjustment(newPowers);
    }, 300);
  };

  const runDetailedAnalysis = async () => {
    setLoading(true);
    try {
      // Appel direct au moteur Python (proxy Vite /api/v4 → :8000). Le proxy
      // Node /api/turpe/optimize-manual transforme le payload et casse le call.
      const response = await fetch('/api/v4/optimize-manual', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          pdl_number: pdlNumber,
          user_id: userId,
          current_powers: currentPowers,
          proposed_powers: powers,
          include_risk_analysis: true,
          include_seasonal_analysis: true
        })
      });

      const result = await response.json();
      if (response.ok && result.success) {
        setOptimizationResult(result);
        // Nouvel "Analyse détaillée" → l'utilisateur n'a pas encore validé cette nouvelle version
        setIsValidated(false);
        toast.success('Analyse terminée');
      } else {
        throw new Error(result.message || 'Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('Erreur analyse manuelle:', error);
      toast.error(error.message || 'Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  // Persiste le choix manuel courant en BDD via le handler parent (qui POST
  // sur /api/turpe/update-analysis-result avec analysis_type='manual_optimization').
  const validateAndSave = () => {
    if (!optimizationResult) {
      toast.error('Lance d\'abord une analyse détaillée avant de valider.');
      return;
    }
    onOptimizationUpdate?.(optimizationResult);
    setIsValidated(true);
    toast.success('Choix manuel enregistré.');
  };

  const resetToOriginal = () => {
    setPowers(currentPowers);
    sendPowerAdjustment(currentPowers);
  };

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <FiWifi className="h-3 w-3 text-green-600" />;
      case 'connecting':
        return <FiLoader className="h-3 w-3 text-blue-600 animate-spin" />;
      case 'error':
        return <FiWifiOff className="h-3 w-3 text-red-600" />;
      default:
        return <FiWifiOff className="h-3 w-3 text-gray-400" />;
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connecté';
      case 'connecting':
        return 'Connexion...';
      case 'error':
        return 'Erreur';
      default:
        return 'Déconnecté';
    }
  };

  if (!pdlNumber || !userId) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-2">
        <div className="text-center py-6 text-gray-500">
          <FiLoader className="animate-spin h-3 w-3 mx-auto mb-1.5" />
          <p className="text-xs">En attente des informations du PDL...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 p-2">
      {/* Header compact */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 p-2 rounded-lg border border-orange-200">
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-xs font-bold text-gray-900 flex items-center">
            <FiSliders className="mr-2 h-3.5 w-3.5 text-orange-600" />
            Optimisation Manuelle
          </h3>
          <div className="flex items-center space-x-2">
            <div className="bg-orange-100 px-2 py-1 rounded text-xs font-medium text-orange-700">
              Manuel
            </div>
            <div className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium ${
              connectionStatus === 'connected' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {getConnectionStatusIcon()}
              <span>{getConnectionStatusText()}</span>
            </div>
          </div>
        </div>
        <p className="text-gray-600 text-xs">
          Ajustez les puissances et voyez l'impact en temps réel
        </p>
      </div>

      {/* Sliders compacts pour 13" */}
      <div className="bg-white p-2 rounded-lg border border-gray-200">
        <h4 className="font-medium text-gray-800 mb-1.5 text-xs">Ajustement des puissances</h4>
        
        <div className="space-y-2">
          {[
            { key: 'hph', label: 'HPH', fullLabel: 'Heures Pleines Hiver', color: 'blue' },
            { key: 'hch', label: 'HCH', fullLabel: 'Heures Creuses Hiver', color: 'indigo' },
            { key: 'hpb', label: 'HPB', fullLabel: 'Heures Pleines Basse Saison', color: 'green' },
            { key: 'hcb', label: 'HCB', fullLabel: 'Heures Creuses Basse Saison', color: 'emerald' }
          ].map(({ key, label, fullLabel, color }) => {
            const maxAtteint = maxPowersByPeriod[key] || 0;
            const psCurrent = currentPowers?.[key] || 0;
            const psNew = powers[key];
            const risqueDepassement = maxAtteint > 0 && psNew < maxAtteint;
            // Min slider = ceil(maxAtteint), arrondi à 6 inférieur pour les paliers standard.
            const sliderMin = Math.max(6, Math.ceil(maxAtteint));
            // Max slider dynamique : 300 par défaut (BT), élargi pour HTA/HTB où
            // PS et max atteint peuvent dépasser plusieurs centaines de kVA
            // (PDL HTA, Caroline 30/06 : max atteint 1365 kW vs slider 300).
            // Formule : max(300, max(PS_actuelle, max_atteint) × 1.2 arrondi
            // au palier 10 supérieur).
            const sliderMax = Math.max(
              300,
              Math.ceil(Math.max(Number(psCurrent) || 0, maxAtteint) * 1.2 / 10) * 10
            );
            return (
              <div key={key} className="space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700">{label}</span>
                    <span className="text-[10px] text-gray-500 hidden sm:inline">{fullLabel}</span>
                    {maxAtteint > 0 && (
                      <span className="text-[10px] text-orange-600 bg-orange-50 border border-orange-200 px-1 py-0 rounded">
                        Max atteint : {maxAtteint.toFixed(1)} kW
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-1.5 text-xs">
                    <span className="text-gray-500">{psCurrent} kVA</span>
                    <span>→</span>
                    <span className={`font-bold ${risqueDepassement ? 'text-red-600' : `text-${color}-600`}`}>{psNew} kVA</span>
                    {psNew !== psCurrent && (
                      psNew > psCurrent ? (
                        <FiTrendingUp className="h-3 w-3 text-red-500" />
                      ) : (
                        <FiTrendingDown className="h-3 w-3 text-green-500" />
                      )
                    )}
                  </div>
                </div>
                <div className="relative">
                  <input
                    type="range"
                    min="6"
                    max={sliderMax}
                    step="1"
                    value={powers[key]}
                    onChange={(e) => handlePowerChange(key, e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                    <span>6</span>
                    {maxAtteint > 0 && (
                      <span className="text-orange-600 font-medium">↑ max {maxAtteint.toFixed(0)}</span>
                    )}
                    <span>{sliderMax}</span>
                  </div>
                  {risqueDepassement && (
                    <p className="text-[10px] text-red-600 mt-0.5">⚠️ PS &lt; max atteint → dépassement attendu</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-4 border-t mt-2 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <button
              onClick={resetToOriginal}
              className="px-1.5 py-2 border border-gray-300 text-gray-700 rounded text-xs hover:bg-gray-50 transition-colors flex items-center"
            >
              <FiRefreshCw className="mr-1 h-3 w-3" />
              Réinitialiser
            </button>
            <div className="flex items-center gap-1 border border-emerald-300 bg-emerald-50 rounded">
              <button
                onClick={() => {
                  // Aligner chaque PS sur le max atteint × (1 + marge/100), arrondi
                  // à l'entier supérieur. Soustraire 1e-6 avant Math.ceil pour ignorer
                  // les imprécisions flottantes (ex: Python retournait 17.000000000000004
                  // → ceil = 18 sans cette protection).
                  const factor = 1 + (Number(safetyMarginPct) || 0) / 100;
                  const safeCeil = (v) => Math.max(6, Math.ceil(((v || 0) * factor) - 1e-6));
                  const aligned = {
                    hph: safeCeil(maxPowersByPeriod.hph),
                    hch: safeCeil(maxPowersByPeriod.hch),
                    hpb: safeCeil(maxPowersByPeriod.hpb),
                    hcb: safeCeil(maxPowersByPeriod.hcb),
                  };
                  setPowers(aligned);
                  sendPowerAdjustment(aligned);
                }}
                disabled={!Object.values(maxPowersByPeriod).some(v => v > 0)}
                className="px-1.5 py-2 text-emerald-700 text-xs hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center rounded-l"
                title="Place chaque PS sur sa puissance max atteinte + marge de sécurité"
              >
                <FiTrendingDown className="mr-1 h-3 w-3" />
                PS = Max + marge
              </button>
              <select
                value={safetyMarginPct}
                onChange={e => setSafetyMarginPct(Number(e.target.value))}
                className="bg-transparent text-emerald-700 text-xs py-1 pr-1 cursor-pointer focus:outline-none border-l border-emerald-300"
                title="Marge de sécurité ajoutée au max atteint"
              >
                <option value={0}>+0 %</option>
                <option value={5}>+5 %</option>
                <option value={10}>+10 %</option>
                <option value={15}>+15 %</option>
                <option value={20}>+20 %</option>
                <option value={25}>+25 %</option>
                <option value={30}>+30 %</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={runDetailedAnalysis}
              disabled={loading || !isConnected}
              className="px-2 py-2 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {loading ? (
                <><FiLoader className="animate-spin h-3 w-3" /><span>Analyse...</span></>
              ) : (
                <><FiPlay className="h-3 w-3" /><span>Analyse détaillée</span></>
              )}
            </button>
            <button
              onClick={validateAndSave}
              disabled={!optimizationResult || isValidated}
              className={`px-2 py-2 rounded text-xs transition-colors flex items-center space-x-2 ${
                isValidated
                  ? 'bg-green-100 text-green-700 border border-green-300 cursor-default'
                  : 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
              title={
                isValidated
                  ? 'Choix manuel enregistré — relance une analyse pour modifier'
                  : 'Sauvegarder ce choix manuel (sera visible au retour sur la page + dans la carte Optimisation Manuelle)'
              }
            >
              <FiCheckCircle className="h-3 w-3" />
              <span>{isValidated ? 'Enregistré ✓' : 'Valider et enregistrer'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Résultats temps réel - Version compacte */}
      {realTimeData && (
        <div className="bg-white p-2 rounded-lg border border-gray-200">
          <h4 className="font-medium text-gray-800 mb-1.5 flex items-center text-xs">
            <FiActivity className="mr-2 h-3 w-3" />
            Impact temps réel
          </h4>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-1.5 mb-1.5">
            <div className="bg-blue-50 rounded p-1.5 text-center">
              <div className="text-xs font-bold text-blue-700">
                {realTimeData.monthly_cost?.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: 'EUR'
                })}
              </div>
              <div className="text-xs text-blue-600">Coût/mois</div>
            </div>
            <div className="bg-green-50 rounded p-1.5 text-center">
              <div className="text-xs font-bold text-green-700">
                {realTimeData.monthly_savings?.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: 'EUR'
                })}
              </div>
              <div className="text-xs text-green-600">Économie/mois</div>
            </div>
            <div className="bg-emerald-50 rounded p-1.5 text-center">
              <div className="text-xs font-bold text-emerald-700">
                {realTimeData.annual_savings?.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: 'EUR'
                })}
              </div>
              <div className="text-xs text-emerald-600">Économie/an</div>
            </div>
            <div className="bg-purple-50 rounded p-1.5 text-center">
              <div className="text-xs font-bold text-purple-700">
                {(realTimeData.viability_score * 100)?.toFixed(1)}%
              </div>
              <div className="text-xs text-purple-600">Score</div>
            </div>
          </div>

          {/* Alertes contraintes - Version compacte */}
          {realTimeData.constraint_violations?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded p-1.5 mb-1.5">
              <div className="flex items-center space-x-2 mb-2">
                <FiAlertTriangle className="h-3 w-3 text-red-600" />
                <span className="font-medium text-red-800 text-xs">Violations de contraintes</span>
              </div>
              <ul className="space-y-1">
                {realTimeData.constraint_violations.map((violation, index) => (
                  <li key={index} className="text-xs text-red-700">
                    • {violation.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-xs text-gray-500 flex items-center justify-between">
            <span>Calcul en {realTimeData.execution_time_ms || 0}ms</span>
            <span>{realTimeData.from_cache ? '(Cache)' : '(Temps réel)'}</span>
          </div>
        </div>
      )}

      {/* Résultats analyse détaillée - Version compacte */}
      {optimizationResult && (
        <div className="bg-white p-2 rounded-lg border border-gray-200">
          <h4 className="font-medium text-gray-800 mb-1.5 text-xs">Analyse détaillée</h4>
          
          <div className="space-y-2">
            {/* Résumé de viabilité compact */}
            <div className={`p-1.5 rounded flex items-center text-xs ${
              optimizationResult.is_viable ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {optimizationResult.is_viable ? 
                <FiCheckCircle className="h-3 w-3 mr-3" /> : 
                <FiAlertTriangle className="h-3 w-3 mr-3" />
              }
              <div>
                <h5 className="font-medium">
                  {optimizationResult.is_viable ? 'Optimisation viable' : 'Optimisation non viable'}
                </h5>
                <p className="text-xs">
                  Score: {(optimizationResult.viability_score * 100).toFixed(1)}% | 
                  Temps: {optimizationResult.execution_time_ms}ms
                </p>
              </div>
            </div>
            
            {/* Analyse d'impact financier - Compact */}
            <div>
              <h5 className="font-medium text-gray-800 mb-2 flex items-center text-xs">
                <FiDollarSign className="mr-2 h-3 w-3"/>
                Impact financier
              </h5>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-1.5">
                <ResultCard 
                  title="Économie annuelle" 
                  value={formatCurrency(optimizationResult.impact_analysis?.annual_savings || 0)} 
                  unit="/an" 
                  colorClass="border-green-500 text-green-600" 
                  icon={<FiTrendingUp/>} 
                />
                <ResultCard 
                  title="Coût actuel" 
                  value={formatCurrency(optimizationResult.current_annual_cost || 0)} 
                  unit="/an" 
                  colorClass="border-red-500 text-red-600" 
                  icon={<FiBarChart2/>} 
                />
                <ResultCard 
                  title="Coût proposé" 
                  value={formatCurrency(optimizationResult.proposed_annual_cost || 0)} 
                  unit="/an" 
                  colorClass="border-blue-500 text-blue-600" 
                  icon={<FiBarChart2/>} 
                />
              </div>
            </div>

            {/* Ajustements de puissance - Compact */}
            {optimizationResult.power_adjustments && optimizationResult.power_adjustments.length > 0 && (
              <div>
                <h5 className="font-medium text-gray-800 mb-2 flex items-center text-xs">
                  <FiZap className="mr-2 h-3 w-3"/>
                  Ajustements de puissance
                </h5>
                <div className="bg-gray-50 p-1.5 rounded border">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-1.5">
                    {optimizationResult.power_adjustments.map(adj => (
                      <div key={adj.period} className="text-center">
                        <p className="font-medium text-indigo-700 text-xs">{adj.period}</p>
                        <p className="text-xs text-gray-500">{adj.current_value} kVA</p>
                        <p className="text-xs font-bold">➔ {adj.proposed_value} kVA</p>
                        <p className={`font-medium text-xs ${adj.adjustment_percent >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {adj.adjustment_percent >= 0 ? '+' : ''}{adj.adjustment_percent.toFixed(1)}%
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Analyse de risque - Compact */}
            {optimizationResult.impact_analysis?.risk_analysis?.length > 0 && (
              <div>
                <h5 className="font-medium text-gray-800 mb-2 flex items-center text-xs">
                  <FiAlertTriangle className="mr-2 h-3 w-3"/>
                  Analyse de risque
                </h5>
                <div className="bg-gray-50 p-1.5 rounded border space-y-2">
                  {optimizationResult.impact_analysis.risk_analysis.map(risk => (
                    <div key={risk.period} className="flex justify-between items-center p-2 rounded bg-orange-50 text-xs">
                      <span className="font-medium text-orange-800">{risk.period}</span>
                      <div className="text-xs">
                        <span>Risque: {risk.proposed_risk_days} j/an</span>
                      </div>
                      <div className="text-right text-xs">
                        <span className="font-medium text-red-600">{formatCurrency(risk.estimated_cmdps_cost)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommandations - Compact */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-1.5">
              {optimizationResult.recommendations && optimizationResult.recommendations.length > 0 && (
                <div className="bg-blue-50 p-1.5 rounded border border-blue-200">
                  <h5 className="font-medium text-blue-800 mb-2 flex items-center text-xs">
                    <FiThumbsUp className="mr-2 h-3 w-3"/>
                    Recommandations
                  </h5>
                  <ul className="list-disc list-inside space-y-1 text-blue-700 text-xs">
                    {optimizationResult.recommendations.map((rec, i) => (
                      <li key={i}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {optimizationResult.next_steps && optimizationResult.next_steps.length > 0 && (
                <div className="bg-yellow-50 p-1.5 rounded border border-yellow-200">
                  <h5 className="font-medium text-yellow-800 mb-2 text-xs">Prochaines étapes</h5>
                  <ul className="list-disc list-inside space-y-1 text-yellow-700 text-xs">
                    {optimizationResult.next_steps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualOptimizationPanel;